// src/components/Jokbo/JokboPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Chip, CircularProgress, Pagination, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, useMediaQuery,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Link as RouterLink } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import api from '../../api/axios';
import JokboTable from './JokboTable';

/* Chip 색상 매핑 */
const traitColorMap = {
    도미넌트: 'error', '마스터/미스트레스': 'error', 오너: 'error', '대디/마미': 'error',
    '브랫 테이머': 'error', 사디스트: 'error', 헌터: 'error', 디그레이더: 'error',
    스팽커: 'error', 리거: 'error',
    서브미시브: 'primary', 슬레이브: 'primary', 펫: 'primary', 리틀: 'primary',
    브랫: 'primary', 마조히스트: 'primary', 프레이: 'primary', 디그레이디: 'primary',
    스팽키: 'primary', 로프버니: 'primary',
};

/* 테이블 헤더 셀 스타일 */
const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 700,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
}));

const ROWS_PER_PAGE = 15;

export default function JokboPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [statusFilter, setStatus] = useState('all');
    const [offlineFilter, setOffline] = useState('all');
    const [genderFilter, setGenderFilter] = useState('all');

    /* ── 즐겨찾기 토글 핸들러 ────────────────── */
    const handleToggleFavorite = async (username, currentStatus) => {
        // 1. UI 즉시 반영 (Optimistic Update)
        setUsers(prevUsers =>
            prevUsers.map(u =>
                u.username === username
                    ? { ...u, is_favorite: !currentStatus }
                    : u
            )
        );

        try {
            // 2. API 호출
            if (currentStatus) {
                // 즐겨찾기 상태 -> 삭제
                await api.delete(`/api/accounts/jokbo-users/${username}/favorite/`);
            } else {
                // 즐겨찾기 아님 -> 추가
                await api.post(`/api/accounts/jokbo-users/${username}/favorite/`);
            }
        } catch (error) {
            console.error("즐겨찾기 변경 실패:", error);
            alert("즐겨찾기 상태 변경에 실패했습니다.");
            // 3. 에러 발생 시 롤백
            setUsers(prevUsers =>
                prevUsers.map(u =>
                    u.username === username
                        ? { ...u, is_favorite: currentStatus } // 원상 복구
                        : u
                )
            );
        }
    };

    /* ── 데이터 로드 ───────────────────────────── */
    useEffect(() => {
        api.get('/api/accounts/jokbo-users/')
            .then(res => setUsers(res.data.users ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    /* ── 필터 옵션 ─────────────────────────────── */
    const recruitOpts = useMemo(
        () => [...new Set(users.map(u => u.recruit_main).filter(Boolean))],
        [users],
    );
    const offlineOpts = useMemo(
        () => [...new Set(users.map(u => u.offline_status).filter(Boolean))],
        [users],
    );
    // [✅ 추가]
    const genderOpts = useMemo(
        () => [...new Set(users.map(u => u.gender).filter(Boolean))],
        [users],
    );

    /* ── 필터링 + 페이지네이션 ──────────────────── */
    const filteredAndSorted = useMemo(() => {
        const filteredUsers = users.filter(u => {
            const q = search.trim().toLowerCase();
            if (
                q &&
                ![u.username, u.nickname, ...(u.detailed_traits ?? [])]
                    .join(' ')
                    .toLowerCase()
                    .includes(q)
            ) return false;

            if (statusFilter !== 'all' && u.recruit_main !== statusFilter) return false;
            if (offlineFilter !== 'all' && u.offline_status !== offlineFilter) return false;
            if (genderFilter !== 'all' && u.gender !== genderFilter) return false;
            return true;
        });

        // 👇 즐겨찾기한 유저(is_favorite: true)를 최상단으로 정렬
        return filteredUsers.sort((a, b) => {
            return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
        });

    }, [users, search, statusFilter, offlineFilter, genderFilter]); // (의존성 배열은 동일)

    // [✅ 5. 'filtered'를 'filteredAndSorted'로 수정]
    const totalPages = Math.ceil(filteredAndSorted.length / ROWS_PER_PAGE) || 1;
    const paginated = filteredAndSorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    /* ── 렌더 ──────────────────────────────────── */
    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                산들 족보 📜
            </Typography>

            {/* ── 검색 · 필터 · 총 인원 ─────────────── */}
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                mb={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ '& > *': { width: { xs: '100%', md: 'auto' } } }}
            >
                <TextField
                    label="검색 (닉네임, 세부 성향)"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    size="small"
                />

                <FormControl size="small">
                    <InputLabel>구인 상태</InputLabel>
                    <Select
                        label="구인 상태"
                        value={statusFilter}
                        onChange={e => { setStatus(e.target.value); setPage(1); }}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        {recruitOpts.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>오프 여부</InputLabel>
                    <Select
                        label="오프 여부"
                        value={offlineFilter}
                        onChange={e => { setOffline(e.target.value); setPage(1); }}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        {offlineOpts.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>성별</InputLabel>
                    <Select
                        label="성별"
                        value={genderFilter}
                        onChange={e => { setGenderFilter(e.target.value); setPage(1); }}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        {genderOpts.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* 총 n 명 – PC: body1(16px), Mobile: body2(14px) */}
                <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    fontWeight={500}
                    sx={{
                        ml: { xs: 0, md: 'auto' },
                        mt: { xs: 1, md: 0 },
                        textAlign: { xs: 'right', md: 'left' },
                        alignSelf: { xs: 'flex-end', md: 'center' },   // PC 수직 중앙
                    }}
                >
                    총&nbsp;{filteredAndSorted.length}&nbsp;명
                </Typography>
            </Stack>

            {/* ── 목록 표 or 모바일 카드 ─────────────── */}
            {isMobile ? (
                <JokboTable
                    userList={paginated}
                    onToggleFavorite={handleToggleFavorite}
                />
            ) : (
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflowX: 'auto',
                        '& thead th': { p: 1.25 },
                        '& tbody td': { p: 1.25 },
                        '& tbody tr:nth-of-type(even)': { bgcolor: theme.palette.grey[50] },
                        '& tbody tr:hover td': { bgcolor: theme.palette.grey[100] },
                    }}
                >
                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                        <colgroup>

                            <col style={{ width: 120 }} /> {/* 닉네임 */}
                            <col style={{ width: 120 }} /> {/* 년생(나이) */}
                            <col style={{ width: 160 }} /> {/* 성별(메인성향) */}
                            <col style={{ width: 240 }} /> {/* 세부 성향 */}
                            <col style={{ width: 110 }} /> {/* 구인 상태 */}
                            <col style={{ width: 150 }} /> {/* 상세 상태 */}
                            <col style={{ width: 120 }} /> {/* 관계 플래그 */}
                            <col style={{ width: 110 }} /> {/* 오프 여부 */}
                            <col style={{ width: 150 }} />  {/* 미니홈 */}
                        </colgroup>

                        <TableHead>
                            <TableRow>
                                <HeaderCell align="center">닉네임</HeaderCell>
                                <HeaderCell align="center">년생(나이)</HeaderCell>
                                <HeaderCell align="center">성별(메인성향)</HeaderCell>
                                <HeaderCell align="left">세부 성향</HeaderCell>
                                <HeaderCell align="center">구인 상태</HeaderCell>
                                <HeaderCell align="center">상세 상태</HeaderCell>
                                <HeaderCell align="center">관계 플래그</HeaderCell>
                                <HeaderCell align="center">오프 여부</HeaderCell>
                                <HeaderCell align="center">미니홈/⭐</HeaderCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginated.length === 0 ? (
                                <TableRow>
                                    {/* [✅ 10. colSpan 9 -> 10으로 수정 (열이 10개가 됨)] */}
                                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                        조건에 맞는 유저가 없습니다.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginated.map(u => {
                                    const birthYear = u.birth_year ?? '-';
                                    const ageDisplay =
                                        (u.birth_year || u.age)
                                            ? `${birthYear}(${u.age ?? '-'})`
                                            : '-';
                                    const recruitDetail = (() => {
                                        if (u.recruit_main === '구인중') {
                                            if (u.recruit_sub === '기타') {
                                                return u.recruit_note?.trim() || '기타';
                                            }
                                            return u.recruit_sub;
                                        }
                                        if (u.recruit_main === '구인안함' && ['플파', '디엣', '연디'].includes(u.recruit_sub)) {
                                            return `${u.recruit_sub}${u.recruit_note?.trim() ? ` (${u.recruit_note})` : ''}`;
                                        }
                                        return u.recruit_sub;
                                    })();
                                    return (
                                        <TableRow key={u.username} hover>


                                            <TableCell align="center">
                                                <Typography fontWeight={600}>
                                                    {u.nickname || u.username}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">{ageDisplay}</TableCell>
                                            <TableCell align="center">{`${u.gender ?? '-'} (${u.preference ?? '-'})`}</TableCell>
                                            <TableCell align="left">
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    {u.detailed_traits?.length ? (
                                                        u.detailed_traits.map(t => (
                                                            <Chip
                                                                key={t}
                                                                label={t}
                                                                color={traitColorMap[t] ?? 'default'}
                                                                size="small"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2">-</Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            {/* 구인 상태 */}
                                            <TableCell align="center">{u.recruit_main?.trim() || '-'}</TableCell>
                                            {/* 상세 상태 */}
                                            <TableCell align="center">{recruitDetail?.trim() || '-'}</TableCell>
                                            {/* 관계 플래그 */}
                                            <TableCell align="center">{u.recruit_flag?.trim() || '-'}</TableCell>
                                            {/* 오프 여부 */}
                                            <TableCell align="center">{u.offline_status?.trim() || '-'}</TableCell>

                                            {/* 바로가기 (미니홈 + 즐겨찾기) */}
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                    {/* 미니홈 버튼 (크기 유지 또는 살짝 조정) */}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        endIcon={<OpenInNewIcon fontSize="inherit" />} // 아이콘 크기는 글자 크기 따름
                                                        component={RouterLink}
                                                        to={`/u/${u.username}/home`}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            px: 1, // 좌우 패딩 유지 (텍스트+아이콘)
                                                            py: 0.5, // 상하 패딩 유지
                                                            lineHeight: 1.5,
                                                            fontSize: '0.8125rem', // small 버튼 기본 폰트 크기 명시 (선택)
                                                        }}
                                                    >
                                                        미니홈
                                                    </Button>

                                                    {/* 즐겨찾기 버튼 (더 작게 조정) */}
                                                    <Button
                                                        size="small" // size는 small 유지
                                                        variant="outlined"
                                                        onClick={() => handleToggleFavorite(u.username, u.is_favorite)}
                                                        sx={{
                                                            minWidth: '30px', // 최소 너비 고정 (더 줄이거나 늘릴 수 있음)
                                                            width: '30px',    // 너비 고정
                                                            height: '30px',   // 높이 고정 (버튼 크기 정사각형 느낌)
                                                            p: 0,             // 내부 패딩 제거 (아이콘이 꽉 차도록)
                                                            lineHeight: 1,
                                                            borderColor: u.is_favorite ? 'warning.main' : undefined,
                                                        }}
                                                    >
                                                        {/* 아이콘 크기 살짝 줄임 */}
                                                        {u.is_favorite
                                                            ? <StarIcon color="warning" sx={{ fontSize: '1.1rem' }} />
                                                            : <StarBorderIcon sx={{ fontSize: '1.1rem' }} />
                                                        }
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── 페이지네이션 ──────────────────────── */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        shape="rounded"
                        color="primary"
                        size={isMobile ? 'small' : 'medium'}
                    />
                </Box>
            )}
        </Box>
    );
}
